import {
  AmbientWorldEventKind,
  ambientWorldCompositionBudget,
  ambientWorldQuietWindows,
  type AmbientWorldEventKind as AmbientWorldEventKindType,
  type AmbientWorldEventPolicy,
} from './fishing-world-catalog'
import { createMicroLifePresentation, type MicroLifePlacement } from './fishing-world-anchors'

/** A deterministic source of entropy supplied by the caller. */
export type FishingWorldRandom = () => number
export type FishingWorldClock = () => number

export type FishingWorldSchedulerCatalog = Readonly<Record<AmbientWorldEventKindType, AmbientWorldEventPolicy>>

export const FishingWorldSchedulerSuppressionReason = {
  InvalidCatalogPolicy: 'invalid-catalog-policy',
  DocumentHidden: 'document-hidden',
  ReducedMotion: 'reduced-motion',
  DeferredRescheduled: 'deferred-rescheduled',
  MajorCadence: 'major-cadence',
  MajorActive: 'major-active',
  EventCadence: 'event-cadence',
  EventCooldown: 'event-cooldown',
  EventAlreadyActive: 'event-already-active',
  ExclusiveCollision: 'exclusive-collision',
  MinorCompositionLimit: 'minor-composition-limit',
  MinorVisualBudget: 'minor-visual-budget',
  QuietWindow: 'quiet-window',
  NoEnabledEvents: 'no-enabled-events',
  InvalidPlacement: 'invalid-placement',
} as const

export type FishingWorldSchedulerSuppressionReason = typeof FishingWorldSchedulerSuppressionReason[keyof typeof FishingWorldSchedulerSuppressionReason]

export type FishingWorldSchedulerLifecycle = Readonly<{
  visible: boolean
  reducedMotion: boolean
}>

export type FishingWorldEventPresentation = Readonly<{
  /** Debug-only deterministic placement for the shoreline encounter. */
  badgerSide?: 'left' | 'right'
  placement?: MicroLifePlacement
}>

export type FishingWorldActiveEvent = Readonly<{
  /** Stable for an event instance, including renderer completion callbacks. */
  id: string
  kind: AmbientWorldEventKindType
  startedAtMs: number
  expiresAtMs: number
  presentation?: FishingWorldEventPresentation
}>

export type FishingWorldSchedulerState = Readonly<{
  active: readonly FishingWorldActiveEvent[]
  nextEligibleAtMs: Readonly<Partial<Record<AmbientWorldEventKindType, number>>>
  cooldownUntilMs: Readonly<Partial<Record<AmbientWorldEventKindType, number>>>
  majorEligibleAtMs: number | null
  quietUntilMs: number | null
  deferred: boolean
}>

export const emptyFishingWorldSchedulerState: FishingWorldSchedulerState = {
  active: [],
  nextEligibleAtMs: {},
  cooldownUntilMs: {},
  majorEligibleAtMs: null,
  quietUntilMs: null,
  deferred: false,
}

/**
 * Removes only the event instance a controlled renderer completed.  A late
 * callback cannot clear a newer event of the same kind because its id differs.
 */
export function completeFishingWorldEvent(
  state: FishingWorldSchedulerState,
  eventId: string,
): FishingWorldSchedulerState {
  const active = state.active.filter((event) => event.id !== eventId)
  return active.length === state.active.length ? state : { ...state, active }
}

export type FishingWorldSchedulerDecision =
  | Readonly<{
    type: 'emitted'
    event: FishingWorldActiveEvent
  }>
  | Readonly<{
    type: 'suppressed'
    reason: FishingWorldSchedulerSuppressionReason
    blockedReasons: readonly FishingWorldSchedulerSuppressionReason[]
  }>

export type FishingWorldSchedulerResult = Readonly<{
  state: FishingWorldSchedulerState
  decision: FishingWorldSchedulerDecision
}>

export type FishingWorldSchedulerInput = Readonly<{
  clock: FishingWorldClock
  random: FishingWorldRandom
  catalog: FishingWorldSchedulerCatalog
  state: FishingWorldSchedulerState
  lifecycle: FishingWorldSchedulerLifecycle
}>

const MAJOR_CADENCE_MINIMUM_MS = 5 * 60_000
const MAJOR_CADENCE_MAXIMUM_MS = 15 * 60_000

function numberInRange(random: FishingWorldRandom, minimum: number, maximum: number): number {
  if (minimum === maximum) return minimum
  return minimum + Math.floor(random() * (maximum - minimum + 1))
}

function cadenceDelay(policy: AmbientWorldEventPolicy, random: FishingWorldRandom): number {
  return numberInRange(random, policy.cadenceMs.minimum, policy.cadenceMs.maximum)
}

function exclusiveCollisionReason(
  candidatePolicy: AmbientWorldEventPolicy,
  activeEvent: FishingWorldActiveEvent,
  catalog: FishingWorldSchedulerCatalog,
): FishingWorldSchedulerSuppressionReason | null {
  const activePolicy = catalog[activeEvent.kind]
  if (candidatePolicy.exclusionClass === 'major-encounter' && activePolicy.exclusionClass === 'major-encounter') {
    return FishingWorldSchedulerSuppressionReason.ExclusiveCollision
  }
  return null
}

function catalogPolicyReason(catalog: FishingWorldSchedulerCatalog): FishingWorldSchedulerSuppressionReason | null {
  for (const policy of Object.values(catalog)) {
    if (
      policy.cadenceMs.minimum <= 0
      || policy.cadenceMs.maximum < policy.cadenceMs.minimum
      || policy.durationMs <= 0
      || policy.cooldownMs < 0
      || !Number.isInteger(policy.visualCost)
      || policy.visualCost < 0
      || (policy.enabled && (policy.rollout !== 'current' || policy.visual === 'none'))
      || (policy.rollout === 'future-reviewed' && policy.enabled)
      || (policy.tier === 'major'
        && (policy.cadenceMs.minimum < MAJOR_CADENCE_MINIMUM_MS || policy.cadenceMs.maximum > MAJOR_CADENCE_MAXIMUM_MS))
    ) {
      return FishingWorldSchedulerSuppressionReason.InvalidCatalogPolicy
    }
  }

  return null
}

function suppress(
  state: FishingWorldSchedulerState,
  reason: FishingWorldSchedulerSuppressionReason,
  blockedReasons: readonly FishingWorldSchedulerSuppressionReason[] = [reason],
): FishingWorldSchedulerResult {
  return { state, decision: { type: 'suppressed', reason, blockedReasons } }
}

function rebaseDeferredSchedule(
  state: FishingWorldSchedulerState,
  nowMs: number,
  catalog: FishingWorldSchedulerCatalog,
  random: FishingWorldRandom,
): FishingWorldSchedulerState {
  const nextEligibleAtMs = { ...state.nextEligibleAtMs }

  for (const kind of Object.values(AmbientWorldEventKind)) {
    const scheduledAtMs = nextEligibleAtMs[kind]
    if (scheduledAtMs !== undefined && scheduledAtMs <= nowMs) {
      nextEligibleAtMs[kind] = nowMs + cadenceDelay(catalog[kind], random)
    }
  }

  const majorEligibleAtMs = state.majorEligibleAtMs !== null && state.majorEligibleAtMs <= nowMs
    ? nowMs + numberInRange(random, MAJOR_CADENCE_MINIMUM_MS, MAJOR_CADENCE_MAXIMUM_MS)
    : state.majorEligibleAtMs

  return { ...state, nextEligibleAtMs, majorEligibleAtMs, deferred: false }
}

/**
 * Advances the ambient-world policy once. The caller owns the loop; this
 * transition deliberately emits at most one event, so a resumed tab cannot
 * replay a backlog as a burst.
 */
export function advanceFishingWorldScheduler(input: FishingWorldSchedulerInput): FishingWorldSchedulerResult {
  const nowMs = input.clock()
  const invalidPolicy = catalogPolicyReason(input.catalog)
  const active = input.state.active.filter((event) => event.expiresAtMs > nowMs)
  const completedMajor = input.state.active.some((event) => event.expiresAtMs <= nowMs && input.catalog[event.kind].tier === 'major')
  const quietUntilMs = completedMajor
    ? Math.max(input.state.quietUntilMs ?? 0, nowMs + ambientWorldQuietWindows.afterMajorMs)
    : input.state.quietUntilMs
  const state: FishingWorldSchedulerState = {
    ...input.state,
    active,
    quietUntilMs,
    deferred: input.state.deferred || completedMajor,
  }

  if (invalidPolicy !== null) return suppress(state, invalidPolicy)

  if (!input.lifecycle.visible) {
    return suppress(
      {
        ...state,
        active: [],
        // Resume from the current clock instead of retaining an elapsed
        // decorative event or replaying its missed cadence.
        deferred: true,
      },
      FishingWorldSchedulerSuppressionReason.DocumentHidden,
    )
  }

  if (state.quietUntilMs !== null && nowMs < state.quietUntilMs) {
    return suppress(
      { ...state, deferred: true },
      FishingWorldSchedulerSuppressionReason.QuietWindow,
    )
  }

  const enabledPolicies = Object.entries(input.catalog)
    .filter(([, policy]) => policy.enabled)
    .sort(([first], [second]) => first.localeCompare(second)) as [AmbientWorldEventKindType, AmbientWorldEventPolicy][]

  const schedulablePolicies = input.lifecycle.reducedMotion
    ? enabledPolicies.filter(([, policy]) => policy.reducedMotion === 'allow')
    : enabledPolicies

  if (input.lifecycle.reducedMotion) {
    const active = state.active.filter((event) => input.catalog[event.kind].reducedMotion === 'allow')
    if (active.length !== state.active.length || schedulablePolicies.length === 0) {
      return suppress(
        { ...state, active, deferred: true },
        FishingWorldSchedulerSuppressionReason.ReducedMotion,
      )
    }
  }

  if (schedulablePolicies.length === 0 && enabledPolicies.length > 0) {
    return suppress(
      { ...state, deferred: true },
      FishingWorldSchedulerSuppressionReason.ReducedMotion,
    )
  }

  if (state.deferred) {
    return suppress(
      rebaseDeferredSchedule(state, nowMs, input.catalog, input.random),
      FishingWorldSchedulerSuppressionReason.DeferredRescheduled,
    )
  }

  if (schedulablePolicies.length === 0) return suppress(state, FishingWorldSchedulerSuppressionReason.NoEnabledEvents)

  const initialMajorEligibility = state.majorEligibleAtMs === null
    && schedulablePolicies.some(([, policy]) => policy.tier === 'major')
    ? nowMs + numberInRange(input.random, MAJOR_CADENCE_MINIMUM_MS, MAJOR_CADENCE_MAXIMUM_MS)
    : state.majorEligibleAtMs
  const schedulingState: FishingWorldSchedulerState = { ...state, majorEligibleAtMs: initialMajorEligibility }

  const activeMinors = schedulingState.active.filter((event) => input.catalog[event.kind].tier === 'minor')
  const activeMinorVisualCost = activeMinors.reduce((total, event) => total + input.catalog[event.kind].visualCost, 0)
  const activeMajor = schedulingState.active.some((event) => input.catalog[event.kind].tier === 'major')
  const candidates: AmbientWorldEventKindType[] = []
  const blockedReasons: FishingWorldSchedulerSuppressionReason[] = []

  for (const [kind, policy] of schedulablePolicies) {
    let reason: FishingWorldSchedulerSuppressionReason | null = null
    if (schedulingState.active.some((event) => event.kind === kind)) {
      reason = FishingWorldSchedulerSuppressionReason.EventAlreadyActive
    } else {
      const collisionReason = schedulingState.active
        .map((event) => exclusiveCollisionReason(policy, event, input.catalog))
        .find((value) => value !== null)
      if (collisionReason !== undefined) reason = collisionReason
    }
    if (reason === null && policy.tier === 'major' && activeMajor) {
      reason = FishingWorldSchedulerSuppressionReason.MajorActive
    } else if (reason === null && policy.tier === 'major' && schedulingState.majorEligibleAtMs !== null && nowMs < schedulingState.majorEligibleAtMs) {
      reason = FishingWorldSchedulerSuppressionReason.MajorCadence
    } else if (reason === null && policy.tier === 'minor' && activeMinors.length >= ambientWorldCompositionBudget.maximumActiveMinors) {
      reason = FishingWorldSchedulerSuppressionReason.MinorCompositionLimit
    } else if (reason === null && policy.tier === 'minor' && activeMinorVisualCost + policy.visualCost > ambientWorldCompositionBudget.maximumMinorVisualCost) {
      reason = FishingWorldSchedulerSuppressionReason.MinorVisualBudget
    } else if (reason === null && (state.cooldownUntilMs[kind] ?? 0) > nowMs) {
      reason = FishingWorldSchedulerSuppressionReason.EventCooldown
    } else if (reason === null && (state.nextEligibleAtMs[kind] ?? nowMs) > nowMs) {
      reason = FishingWorldSchedulerSuppressionReason.EventCadence
    }

    if (reason === null) candidates.push(kind)
    else if (!blockedReasons.includes(reason)) blockedReasons.push(reason)
  }

  if (candidates.length === 0) {
    return suppress(schedulingState, blockedReasons[0] ?? FishingWorldSchedulerSuppressionReason.NoEnabledEvents, blockedReasons)
  }

  const kind = candidates[Math.floor(input.random() * candidates.length)]
  const policy = input.catalog[kind]
  const placement = createMicroLifePresentation(kind, `${kind}:${nowMs}`)
  if (placement === null || placement.id !== policy.placement) {
    return suppress(schedulingState, FishingWorldSchedulerSuppressionReason.InvalidPlacement)
  }
  const event: FishingWorldActiveEvent = {
    id: `${kind}:${nowMs}`,
    kind,
    startedAtMs: nowMs,
    expiresAtMs: nowMs + policy.durationMs,
    presentation: {
      placement,
      ...(kind === AmbientWorldEventKind.Badger
        ? { badgerSide: placement.orientation === 'facing-left' ? 'right' as const : 'left' as const }
        : {}),
    },
  }
  const nextEligibleAtMs = {
    ...state.nextEligibleAtMs,
    [kind]: nowMs + cadenceDelay(policy, input.random),
  }
  const cooldownUntilMs = {
    ...schedulingState.cooldownUntilMs,
    [kind]: event.expiresAtMs + policy.cooldownMs,
  }
  const nextState: FishingWorldSchedulerState = {
    ...schedulingState,
    active: [...schedulingState.active, event],
    nextEligibleAtMs,
    cooldownUntilMs,
    majorEligibleAtMs: policy.tier === 'major'
      ? nowMs + numberInRange(input.random, MAJOR_CADENCE_MINIMUM_MS, MAJOR_CADENCE_MAXIMUM_MS)
      : schedulingState.majorEligibleAtMs,
  }

  return { state: nextState, decision: { type: 'emitted', event } }
}

/** Mulberry32 is compact, seeded, and deterministic across browser and Node runtimes. */
export function createFishingWorldSeededRandom(seed: number): FishingWorldRandom {
  let state = seed >>> 0
  return () => {
    state += 0x6D2B79F5
    let value = state
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296
  }
}
